// BE status codes (be#451 §D, `statusLabel` UPPER_SNAKE) -> UI label + tone,
// plus the derived turn/permission rules the room and workspace share. Since
// be#455 a partner holds alpha `lines[]`; rounds, turn and accept are per line
// (`PARTNER_STATUS` codes are reused for lines).

export const PACKAGE_STATUS = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  DONE: 'DONE',
};

export const CARGO_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  UNDER_NEGOTIATION: 'UNDER_NEGOTIATION',
  PRICE_ACCEPTED: 'PRICE_ACCEPTED',
  CLOSED: 'CLOSED',
};

export const PARTNER_STATUS = {
  ACTIVE: 'ACTIVE',
  ACCEPTED: 'ACCEPTED',
  WITHDRAWN: 'WITHDRAWN',
  OVERDUE: 'OVERDUE',
};

// `waitingFor` values and the matching `senderSide` ints.
export const SIDE = { PERTAMINA: 'PERTAMINA', MITRA: 'MITRA' };
export const SENDER_SIDE = { PERTAMINA: 1, MITRA: 2, SYSTEM: 3 };

// Display-only codes derived from partner.waitingFor.
const WAITING_FOR_BUYER = 'WAITING_FOR_BUYER';
const WAITING_FOR_SELLER = 'WAITING_FOR_SELLER';

const STATUS_META = {
  OPEN: { label: 'Open', tone: 'muted' },
  IN_PROGRESS: { label: 'In Progress', tone: 'info' },
  DONE: { label: 'Done', tone: 'success' },
  NOT_STARTED: { label: 'Not Started', tone: 'muted' },
  UNDER_NEGOTIATION: { label: 'Under Negotiation', tone: 'primary' },
  PRICE_ACCEPTED: { label: 'Price Accepted', tone: 'success' },
  CLOSED: { label: 'Closed', tone: 'muted' },
  ACTIVE: { label: 'Active', tone: 'primary' },
  ACCEPTED: { label: 'Accepted', tone: 'success' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'danger' },
  OVERDUE: { label: 'Overdue', tone: 'warning' },
  [WAITING_FOR_BUYER]: { label: 'Waiting for Buyer', tone: 'warning' },
  [WAITING_FOR_SELLER]: { label: 'Waiting for Seller', tone: 'info' },
};

export const getStatusMeta = code =>
  STATUS_META[code] || { label: code || '-', tone: 'muted' };

const sideOfMode = mode => (mode === 'supplier' ? SIDE.MITRA : SIDE.PERTAMINA);

const senderSideOfMode = mode =>
  mode === 'supplier' ? SENDER_SIDE.MITRA : SENDER_SIDE.PERTAMINA;

// Whose turn it is on one alpha line (or a partner summary: both carry
// `statusLabel` + `waitingFor`). BE sends null until the cargo starts (then
// PERTAMINA: the buyer opens with a target, be#451 T-a).
export const getTurn = item =>
  item?.statusLabel === PARTNER_STATUS.ACTIVE ? item.waitingFor || null : null;

// Badge for a partner or a line: an active one shows whose turn it is.
export const getPartnerDisplayStatus = item => {
  const turn = getTurn(item);
  if (turn === SIDE.PERTAMINA) return WAITING_FOR_BUYER;
  if (turn === SIDE.MITRA) return WAITING_FOR_SELLER;
  return item?.statusLabel || null;
};

export const isCargoRunning = cargo =>
  cargo?.statusLabel === CARGO_STATUS.UNDER_NEGOTIATION &&
  Boolean(cargo?.deadline);

export const isCargoExpired = (cargo, nowMs) =>
  isCargoRunning(cargo) && Date.parse(cargo.deadline) <= nowMs;

// A withdrawn round stays in the history but never counts for the turn, the
// last price or accept (be#455 K-5).
export const activeRounds = line =>
  (line?.rounds || []).filter(r => !r.isWithdrawn);

// Seconds left to withdraw the caller's last round on this line (0 = none).
// `withdrawableUntil` is server time; `nowMs` is already server-aligned.
export const withdrawRoundSecondsLeft = (line, nowMs) => {
  if (!line?.withdrawableRoundId) return 0;
  const until = Date.parse(line.withdrawableUntil);
  if (!Number.isFinite(until)) return 0;
  return Math.max(0, Math.ceil((until - nowMs) / 1000));
};

// What the caller may do on one alpha line of a partner room right now.
export const getLinePermissions = ({ cargo, partner, line, mode, nowMs }) => {
  const running = isCargoRunning(cargo) && !isCargoExpired(cargo, nowMs);
  const partnerOpen =
    Boolean(partner) && partner.statusLabel !== PARTNER_STATUS.WITHDRAWN;
  const live =
    running && partnerOpen && line?.statusLabel === PARTNER_STATUS.ACTIVE;
  const rounds = activeRounds(line);
  const last = rounds[rounds.length - 1];
  const myTurn = getTurn(line) === sideOfMode(mode);

  return {
    live,
    myTurn,
    canRound: live && myTurn && (rounds.length > 0 || mode !== 'supplier'),
    canAccept:
      live &&
      ((mode !== 'supplier' && rounds.length === 0 && myTurn) ||
        (Boolean(last) && last.senderSide !== senderSideOfMode(mode))),
    canWithdrawRound: live && withdrawRoundSecondsLeft(line, nowMs) > 0,
    canWithdrawPartner:
      running &&
      partnerOpen &&
      (partner.lines || []).some(l => l.statusLabel === PARTNER_STATUS.ACTIVE),
  };
};

// Round price check before the confirm dialog. Alpha may be 0 or negative;
// the only bound is "lower than the Mitra Last Offer" (be#455 K-4, the BE
// still answers 400). `fmt` formats the bound for the message.
export const validateRoundPrice = (raw, mitraLastOffer, fmt = String) => {
  const text = String(raw ?? '').trim();
  if (!text) return { price: null, error: 'Please enter a price.' };
  const price = Number(text);
  if (!Number.isFinite(price)) {
    return { price: null, error: 'Please enter a valid number.' };
  }
  if (mitraLastOffer != null && price >= Number(mitraLastOffer)) {
    return {
      price,
      error: `The price must be lower than the Mitra Last Offer (${fmt(mitraLastOffer)}).`,
    };
  }
  return { price, error: null };
};

// Default line when a room opens: the first active line waiting for the
// viewer, else the first line.
export const pickDefaultLine = (lines = [], mode) =>
  lines.find(l => getTurn(l) === sideOfMode(mode)) || lines[0] || null;

// Buyer only: extend while a line can still negotiate (be#455 C).
export const canExtendCargo = cargo =>
  Boolean(cargo?.startedAt) &&
  (cargo.partners || []).some(p =>
    (p.lines || []).some(
      l =>
        l.statusLabel === PARTNER_STATUS.ACTIVE ||
        l.statusLabel === PARTNER_STATUS.OVERDUE
    )
  );

const bySort = key => (a, b) => (a?.[key] ?? 0) - (b?.[key] ?? 0);

// A withdrawn round frees its roundNo, so a later round can reuse it.
const byRound = (a, b) =>
  (a?.roundNo ?? 0) - (b?.roundNo ?? 0) ||
  (Date.parse(a?.createdDate) || 0) - (Date.parse(b?.createdDate) || 0);

// `Cargo A|P1` is display-only, from the partners[] order; a lone partner is
// just `Cargo A`.
export const partnerLabel = (cargoName, index, total) =>
  total > 1 ? `${cargoName}|P${index + 1}` : cargoName;

// Sorts children and stamps the server clock offset used by every timer.
export const normalizeDetail = (data, clientNowMs = Date.now()) => {
  if (!data) return null;
  const serverMs = Date.parse(data.serverNow);
  const cargoes = [...(data.cargoes || [])].sort(bySort('sortOrder'));

  return {
    ...data,
    clockOffsetMs: Number.isFinite(serverMs) ? serverMs - clientNowMs : 0,
    cargoes: cargoes.map(cargo => {
      const partners = [...(cargo.partners || [])].sort(bySort('sortOrder'));
      return {
        ...cargo,
        partners: partners.map((partner, index) => ({
          ...partner,
          label: partnerLabel(cargo.cargoName, index, partners.length),
          lines: [...(partner.lines || [])]
            .sort(bySort('sortOrder'))
            .map(line => ({
              ...line,
              rounds: [...(line.rounds || [])].sort(byRound),
            })),
        })),
      };
    }),
  };
};
